/**
 * @fileoverview Nav module for navigation actions.
 */

import { postToHost, onMessage } from "../../core/messenger";
import { logger } from "../../core/logger";
import {
  validateNavAction,
  validateEmbeddedNavAddItem,
  validateEmbeddedNavPatchItem,
  validateEmbeddedNavRemoveValue,
  logValidationErrors,
} from "../../core/validation";
import { handleResponse, sendRequest } from "../../core/requests";
import { createSubscription } from "../../core/subscriptions";
import type {
  BaseMessage,
  NavActionClickMessage,
  Unsubscribe,
} from "../../core/types";
import { NAV_EVENTS } from "./events";
import type {
  NavModule,
  PrimaryActionConfig,
  ActionClickCallback,
  NavItemInput,
  NavItemPatchInput,
  NavItemAddResult,
  NavItemClickCallback,
} from "./types";

export type {
  NavModule,
  PrimaryActionConfig,
  NavItemInput,
  NavItemNodeInput,
  NavItemChildInput,
  NavItemPatchInput,
  NavItemAddResult,
  NavItemClickCallback,
} from "./types";

type NavItemClickMessage = BaseMessage & {
  event: "embedded::nav.itemClick";
  payload: { id: string; value: string; url: string };
};

/**
 * Create the nav module.
 */
export function createNavModule(): NavModule {
  const clickSubscription = createSubscription<ActionClickCallback>();
  const navItemClickSubscription = createSubscription<NavItemClickCallback>();

  const unsubscribers: Unsubscribe[] = [];

  unsubscribers.push(
    onMessage<NavActionClickMessage>(NAV_EVENTS.ACTION_CLICK, (data) => {
      clickSubscription.notify(data.payload.value);
    }),
  );

  unsubscribers.push(
    onMessage<BaseMessage>(NAV_EVENTS.ADD_ITEM_RESPONSE, (msg) => {
      if (!msg.requestId) return;

      const payload = msg.payload as {
        item?: NavItemAddResult;
        error?: string;
      };

      if (typeof payload.error === "string" && payload.error.trim()) {
        const errText = payload.error.trim();
        logger.warn(`embedded::nav.addItem failed: ${errText}`);
        handleResponse<NavItemAddResult>(
          msg.requestId,
          {} as NavItemAddResult,
          errText,
        );
        return;
      }

      const responseItem = payload.item;
      if (
        responseItem &&
        typeof responseItem.value === "string" &&
        responseItem.value.trim()
      ) {
        const v = responseItem.value.trim();
        const normalized: NavItemAddResult = {
          value: v,
          id: v,
        };
        handleResponse<NavItemAddResult>(msg.requestId, normalized);
      }
    }),
  );

  unsubscribers.push(
    onMessage<NavItemClickMessage>(NAV_EVENTS.ITEM_CLICK, (msg) => {
      const p = msg.payload;
      const v =
        typeof p.value === "string" && p.value.trim()
          ? p.value.trim()
          : typeof p.id === "string" && p.id.trim()
            ? p.id.trim()
            : "";
      if (!v) {
        navItemClickSubscription.notify(p);
        return;
      }
      navItemClickSubscription.notify({
        ...p,
        value: v,
        id: v,
      });
    }),
  );

  return {
    setAction(config: PrimaryActionConfig): void {
      const validation = validateNavAction(config);
      if (!validation.valid) {
        logValidationErrors(NAV_EVENTS.SET_ACTION, validation.errors);
        return;
      }

      postToHost(NAV_EVENTS.SET_ACTION, {
        title: config.title,
        value: config.value,
        subTitle: config.subTitle,
        icon: config.icon,
        disabled: config.disabled,
        extendedActions: config.extendedActions?.map((ext) => ({
          title: ext.title,
          value: ext.value,
          subTitle: ext.subTitle,
          icon: ext.icon,
          disabled: ext.disabled,
        })),
      });
    },

    clearAction(): void {
      postToHost(NAV_EVENTS.CLEAR_ACTION, {});
    },

    onActionClick(callback: ActionClickCallback): Unsubscribe {
      return clickSubscription.subscribe(callback);
    },

    addNavItem(item: NavItemInput): Promise<NavItemAddResult> {
      const validation = validateEmbeddedNavAddItem(item);
      if (!validation.valid) {
        logValidationErrors(NAV_EVENTS.ADD_ITEM, validation.errors);
        return Promise.reject(new Error(validation.errors[0]));
      }
      // sendRequest always rejects with an Error; no extra wrapping needed.
      return sendRequest<NavItemAddResult>(NAV_EVENTS.ADD_ITEM, { item }, 2000);
    },

    updateNavItem(item: NavItemPatchInput): void {
      const validation = validateEmbeddedNavPatchItem(item);
      if (!validation.valid) {
        logValidationErrors(NAV_EVENTS.UPDATE_ITEM, validation.errors);
        return;
      }
      // Validator guarantees `value` and `id` (when both present) are equal
      // and at least one is a non-empty trimmed string.
      const raw = item as NavItemPatchInput & { id?: string };
      const trimmedValue =
        typeof raw.value === "string" ? raw.value.trim() : "";
      const trimmedId = typeof raw.id === "string" ? raw.id.trim() : "";
      const value = trimmedValue || trimmedId;
      const { id: _legacyId, ...rest } = raw;
      void _legacyId;
      postToHost(NAV_EVENTS.UPDATE_ITEM, {
        item: { ...rest, value },
      });
    },

    removeNavItem(value: string): void {
      const validation = validateEmbeddedNavRemoveValue(value);
      if (!validation.valid) {
        logValidationErrors(NAV_EVENTS.REMOVE_ITEM, validation.errors);
        return;
      }
      postToHost(NAV_EVENTS.REMOVE_ITEM, { value });
    },

    onNavItemClick(callback: NavItemClickCallback): Unsubscribe {
      return navItemClickSubscription.subscribe(callback);
    },

    destroy(): void {
      unsubscribers.forEach((unsub) => unsub());
      unsubscribers.length = 0;
      clickSubscription.clear();
      navItemClickSubscription.clear();
    },
  };
}
